/**
 * Reconcile previously-applied edits (playbook redlines and risk fixes)
 * against the LIVE state of the document, so the task pane reflects what the
 * user has since done in Word's Review pane.
 *
 * This mirrors the Legal Connection add-in's redline/changeStatus service:
 * an applied edit is classified against the current tracked changes + body
 * text as one of:
 *
 *   - 'pending'   the tracked change is still in the document — the user has
 *                 not yet accepted or rejected it. Stays "applied".
 *   - 'accepted'  the tracked change is gone and the rewritten text is in the
 *                 body — the user accepted it. Stays "applied".
 *   - 'rejected'  the tracked change is gone and the ORIGINAL (anchor) text is
 *                 back — the user rejected it. We RE-SURFACE the edit so it can
 *                 be applied again.
 *   - 'unknown'   the tracked change is gone and neither rewritten nor anchor
 *                 can be found (edited away / moved). Treated as still applied
 *                 so we never silently lose an edit.
 *
 * Reading is done in a single Word.run pass. We prefer Word's native
 * `body.getTrackedChanges()` (WordApi 1.6); on older hosts we fall back to the
 * OOXML-based scan already in wordApi.ts.
 */
import { isWordApiAvailable, getDocumentText, getTrackedChanges } from './wordApi';

export type EditStatus = 'pending' | 'accepted' | 'rejected' | 'unknown';

const DOC_CHANGED_KEY = 'ezylex:docChanged';

/**
 * Signal that the document was changed by an apply/reject in one pane. Panes
 * are mounted one-at-a-time (see HomeScreen), so a live CustomEvent alone is
 * lost when the other pane is unmounted. We ALSO persist a timestamp so the
 * pane can detect the change when it next mounts (e.g. the Risk pane flags its
 * analysis stale after a redline was applied on the Redlining pane).
 */
export function markDocumentChanged(): void {
  try {
    sessionStorage.setItem(DOC_CHANGED_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent('ezylex:document-changed'));
  } catch {
    /* ignore */
  }
}

/** Timestamp (ms) of the last document change, or 0 if none recorded. */
export function getDocumentChangedAt(): number {
  try {
    return parseInt(sessionStorage.getItem(DOC_CHANGED_KEY) || '0', 10) || 0;
  } catch {
    return 0;
  }
}

/** The minimal edit shape needed to classify — anchor = original text, rewritten = new text. */
export interface ClassifiableEdit {
  anchor: string;
  rewritten: string;
}

export type EditWithStatus<T> = T & { status: EditStatus };

interface DocStateSnapshot {
  bodyText: string;
  trackedTexts: string[];
  // True only when the tracked-change list is COMPLETE and trustworthy (the
  // native body.getTrackedChanges() read succeeded). When true, an applied edit
  // that is neither a live tracked change nor present in the body can be
  // confidently called 'rejected'. The OOXML fallback can silently miss changes
  // on large docs, so it sets this false and we stay conservative.
  reliable: boolean;
}

/**
 * Read body text + the text of every active tracked change in one pass.
 * Returns null when the host is unavailable so callers can no-op safely.
 */
async function loadDocStateSnapshot(): Promise<DocStateSnapshot | null> {
  if (!isWordApiAvailable()) return null;

  // Preferred path: native tracked-change API (WordApi 1.6). One round-trip.
  try {
    return await Word.run(async (ctx: Word.RequestContext) => {
      const body = ctx.document.body;
      body.load('text');
      const tracked = (body as any).getTrackedChanges();
      ctx.load(tracked, 'text');
      await ctx.sync();
      return {
        bodyText: (body.text || '').trim(),
        trackedTexts: ((tracked.items as Array<{ text?: string }>) || [])
          .map((t) => (t.text || '').trim())
          .filter((s) => s.length > 0),
        reliable: true,
      };
    });
  } catch {
    // Fallback for hosts without the native API: body text + OOXML revision scan.
    try {
      const bodyText = await getDocumentText().catch(() => '');
      const changes = await getTrackedChanges().catch(() => []);
      const trackedTexts = changes
        .flatMap((c) => [c.revisedText, c.originalText])
        .map((s) => (s || '').trim())
        .filter((s) => s.length > 0);
      return { bodyText: (bodyText || '').trim(), trackedTexts, reliable: false };
    } catch {
      return null;
    }
  }
}

/**
 * Classify one edit against a doc-state snapshot. Pure (no Office.js calls).
 *
 * Order matters:
 *   1. a tracked change's text still matches `rewritten`      → pending
 *   2. body text contains `rewritten`                         → accepted
 *   3. reliable read, and neither of the above                → rejected
 *      (the suggested text is no longer in effect and there is no live tracked
 *      change for it — so it was rejected/undone). We do NOT require the
 *      original anchor to be re-found: the AI's anchor is frequently not a
 *      verbatim substring of the document, which used to leave a genuinely
 *      rejected redline stuck showing "Applied".
 *   4. unreliable read: fall back to the anchor test, else unknown (keep).
 *
 * All matching is whitespace-normalised: the stored anchor/rewrite carry the
 * source document's line breaks, but Word's body.text joins paragraphs with
 * \r and drops soft wraps, so an exact substring test would miss an otherwise
 * identical passage. Tracked-change matching is bidirectional because an
 * insertion may be split across paragraphs.
 */
export function classifyEditAgainstSnapshot(
  edit: ClassifiableEdit,
  state: DocStateSnapshot
): EditStatus {
  const norm = (s: string) => (s || '').replace(/\s+/g, ' ').trim();
  const rewritten = norm(edit.rewritten);
  const anchor = norm(edit.anchor);
  const bodyText = norm(state.bodyText);
  const trackedTexts = state.trackedTexts.map(norm);

  if (rewritten && trackedTexts.some((t) => t.includes(rewritten) || rewritten.includes(t))) {
    return 'pending';
  }
  if (rewritten && bodyText.includes(rewritten)) {
    return 'accepted';
  }
  // The suggested text is neither pending nor present. With a reliable read we
  // can call it rejected without re-finding the (often non-verbatim) anchor.
  if (rewritten && state.reliable) {
    return 'rejected';
  }
  if (anchor && bodyText.includes(anchor)) {
    return 'rejected';
  }
  return 'unknown';
}

/**
 * Classify a list of previously-applied edits against the current document.
 * One Word.run for the whole list. If the host is unavailable every edit is
 * classified 'unknown' (kept applied) rather than failing.
 */
export async function getEditStatuses<T extends ClassifiableEdit>(
  edits: T[]
): Promise<Array<EditWithStatus<T>>> {
  if (edits.length === 0) return [];
  const state = await loadDocStateSnapshot();
  if (!state) return edits.map((e) => ({ ...e, status: 'unknown' as const }));
  return edits.map((e) => ({ ...e, status: classifyEditAgainstSnapshot(e, state) }));
}

/**
 * Subscribe to document changes so the pane can re-reconcile when the user
 * accepts/rejects a tracked change or edits the document.
 *
 * Two complementary signals are wired (mirrors the Legal Connection add-in's
 * registerEditDetection) because window `focus` / `visibilitychange` do NOT
 * fire for in-document actions inside Word Online — those events only cover
 * the whole browser tab, so "reject in the ribbon" was never detected:
 *
 *   1. `Office.EventType.DocumentSelectionChanged` — a UNIVERSAL Office common
 *      event supported on every host (incl. Word on the web). Fires when the
 *      cursor/selection moves, which happens right after the user clicks
 *      Accept/Reject or edits text. This is the reliable trigger.
 *   2. Word's `onParagraphChanged` / `onParagraphAdded` / `onParagraphDeleted`
 *      (WordApi 1.5+) for direct content edits, when available.
 *
 * The handler is debounced (~500ms) so the three sources coalesce. The
 * returned function unsubscribes both. All failures are swallowed — this is a
 * best-effort live signal.
 */
export function subscribeDocumentChanges(handler: () => void): () => void {
  let last = 0;
  const fire = () => {
    const now = Date.now();
    if (now - last < 500) return;
    last = now;
    try {
      handler();
    } catch {
      /* ignore */
    }
  };

  // 1) Universal selection-changed event (reliable in Word Online).
  let selRegistered = false;
  try {
    Office.context.document.addHandlerAsync(
      Office.EventType.DocumentSelectionChanged,
      fire,
      () => {
        selRegistered = true;
      }
    );
  } catch {
    /* host without the common API — Word events below still carry it */
  }

  // 2) Word paragraph events for direct content edits (WordApi 1.5+).
  let removed = false;
  let removeWord: (() => void) | null = null;
  try {
    Word.run(async (ctx: Word.RequestContext) => {
      const doc = ctx.document as any;
      if (!doc.onParagraphChanged) return;
      const results = [
        doc.onParagraphChanged.add(async () => fire()),
        doc.onParagraphAdded?.add?.(async () => fire()),
        doc.onParagraphDeleted?.add?.(async () => fire()),
      ].filter(Boolean);
      await ctx.sync();
      removeWord = () => {
        for (const er of results) {
          try {
            Word.run(er.context, async (c: Word.RequestContext) => {
              er.remove();
              await c.sync();
            });
          } catch {
            /* ignore */
          }
        }
      };
      if (removed) removeWord();
    }).catch(() => {
      /* onParagraphChanged unsupported — selection event carries it */
    });
  } catch {
    /* ignore */
  }

  return () => {
    removed = true;
    if (selRegistered) {
      try {
        Office.context.document.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          { handler: fire }
        );
      } catch {
        /* ignore */
      }
    }
    if (removeWord) removeWord();
  };
}
